import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Stack, Typography, Card, Chip, Select, MenuItem, FormControl } from "@mui/material";
import { keyframes } from "@mui/system";
import MapIcon from "@mui/icons-material/Map";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { PageHead, pageContainerSx } from "../../components/ui";

const timeAgo = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d > 6)
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  if (d >= 1) return `${d}d ago`;
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return m >= 1 ? `${m}m ago` : "just now";
};

// Geometry for the decorative placeholder floor-plan. Precomputed so each
// element keys on its own coordinates rather than an array index.
const PLACEHOLDER_V_LINES = [0, 50, 100, 150, 200, 250, 300];
const PLACEHOLDER_H_LINES = [0, 50, 100];
const PLACEHOLDER_ROOMS = [
  { x: 30, y: 18, width: 80, height: 46 },
  { x: 130, y: 18, width: 90, height: 46 },
  { x: 30, y: 80, width: 120, height: 34 },
];

// Decorative fallback floor-plan (SVG) used when there's no real PNG preview.
const PlaceholderThumb = () => (
  <>
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 300 130"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, opacity: 0.5 }}
    >
      {PLACEHOLDER_V_LINES.map((x) => (
        <line key={`v${x}`} x1={x} y1={0} x2={x} y2={130} stroke={tokens.border} strokeWidth={1} />
      ))}
      {PLACEHOLDER_H_LINES.map((y) => (
        <line key={`h${y}`} x1={0} y1={y} x2={300} y2={y} stroke={tokens.border} strokeWidth={1} />
      ))}
    </svg>
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 300 130"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0 }}
    >
      {PLACEHOLDER_ROOMS.map((room) => (
        <rect
          key={`${room.x}-${room.y}`}
          x={room.x}
          y={room.y}
          width={room.width}
          height={room.height}
          rx={5}
          fill={tokens.surface3}
          stroke={tokens.border}
        />
      ))}
    </svg>
  </>
);

const MapThumb = ({ map }) => (
  <Box
    sx={{
      position: "relative",
      height: 130,
      background: tokens.surface2,
      borderBottom: `1px solid ${tokens.border}`,
      overflow: "hidden",
    }}
  >
    {map.thumb && map.thumbType === "png" ? (
      <img
        src={`data:image/png;base64,${map.thumb}`}
        alt={map.name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    ) : (
      <PlaceholderThumb />
    )}
  </Box>
);

const MapCard = ({ map, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      overflow: "hidden",
      cursor: "pointer",
      transition: "all .13s ease",
      "&:hover": {
        borderColor: tokens.borderStrong,
        transform: "translateY(-1px)",
        boxShadow: tokens.shadowLg,
      },
    }}
  >
    <MapThumb map={map} />
    <Box sx={{ p: "12px 14px" }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: tokens.text,
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {map.name}
        </Typography>
        <Chip
          icon={<PlaceIcon sx={{ fontSize: "13px !important" }} />}
          label={`${map.markerCount} pts`}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            background: tokens.accentSoft,
            color: tokens.accent2,
            "& .MuiChip-icon": { color: tokens.accent2 },
          }}
        />
      </Stack>
      <Typography sx={{ fontSize: 11.5, color: tokens.text3, mt: 0.4 }}>
        {[map.venue, map.floor].filter(Boolean).join(" · ") || "Unassigned"}
      </Typography>
      <Stack direction="row" gap={2} sx={{ mt: 1.25 }}>
        <Stack direction="row" alignItems="center" gap={0.6} sx={{ color: tokens.text2 }}>
          <SmartToyIcon sx={{ fontSize: 13, color: tokens.text3 }} />
          <Typography sx={{ fontSize: 11.5 }}>
            {map.robotCount} robot{map.robotCount === 1 ? "" : "s"}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.6} sx={{ color: tokens.text2 }}>
          <ScheduleIcon sx={{ fontSize: 13, color: tokens.text3 }} />
          <Typography sx={{ fontSize: 11.5 }}>{timeAgo(map.updatedAt)}</Typography>
        </Stack>
      </Stack>
    </Box>
  </Card>
);

// ── Loading skeleton ────────────────────────────────────────────────────────
// Card skeletons reuse the floor-plan PlaceholderThumb so the loading grid already
// reads as "maps". Only the card grid is skeletonized — the KPI row renders '—'.
const shimmer = keyframes`100% { transform: translateX(100%); }`;

// A grey block that shimmers. `variant` picks a shape via sx overrides.
const Sk = ({ sx }) => (
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      borderRadius: "6px",
      background: tokens.surface3,
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        transform: "translateX(-100%)",
        background: `linear-gradient(90deg, transparent, ${tokens.surface2}, transparent)`,
        animation: `${shimmer} 1.35s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      },
      ...sx,
    }}
  />
);

// Stable keys for the placeholder cards (no map data exists yet at that point).
const MAP_SKELETON_KEYS = ["sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6"];

const MapCardSkeleton = () => (
  <Card sx={{ overflow: "hidden" }}>
    {/* faint floor-plan thumb + a light sweep so it looks like a map loading */}
    <Box
      sx={{
        position: "relative",
        height: 130,
        background: tokens.surface2,
        borderBottom: `1px solid ${tokens.border}`,
        overflow: "hidden",
      }}
    >
      <PlaceholderThumb />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          transform: "translateX(-100%)",
          background: `linear-gradient(90deg, transparent, ${tokens.surface3}, transparent)`,
          animation: `${shimmer} 1.6s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    </Box>
    <Box
      sx={{
        p: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 1.1,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Sk sx={{ flex: 1, height: 14 }} />
        <Sk sx={{ width: 52, height: 20, borderRadius: "999px" }} />
      </Stack>
      <Sk sx={{ width: "60%", height: 11 }} />
      <Stack direction="row" gap={2} sx={{ mt: 0.25 }}>
        <Sk sx={{ width: 64, height: 11 }} />
        <Sk sx={{ width: 64, height: 11 }} />
      </Stack>
    </Box>
  </Card>
);

const Maps = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Venue filter, driven by ?venue=<id> (set either by the "View maps" button
  // on a venue card, or by the dropdown on this page — both use the same param).
  const venueFilterId = searchParams.get("venue");

  useEffect(() => {
    const fetchMaps = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/v1/maps/portal-view`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setMaps(await res.json());
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    fetchMaps();
  }, []);

  // Unique venues present in the map list (for the filter dropdown).
  const venueOptions = [
    ...new Map(
      maps
        .filter((m) => m.venueId)
        .map((m) => [
          String(m.venueId),
          { id: String(m.venueId), name: m.venue || "Unnamed venue" },
        ])
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const setVenueFilter = (id) => setSearchParams(id ? { venue: id } : {});
  const venueFilterName =
    venueOptions.find((v) => v.id === venueFilterId)?.name || searchParams.get("name");

  const filteredMaps = venueFilterId
    ? maps.filter((m) => String(m.venueId) === String(venueFilterId))
    : maps;

  const totalMarkers = filteredMaps.reduce((s, m) => s + (m.markerCount || 0), 0);
  const totalRobots = filteredMaps.reduce((s, m) => s + (m.robotCount || 0), 0);

  // Three-state card grid: skeletons while loading, empty-state, then the real
  // cards. Extracted from a nested ternary in the JSX below; branch order and
  // output are unchanged.
  const renderMapGrid = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 1.5,
          }}
        >
          {MAP_SKELETON_KEYS.map((skeletonKey) => (
            <MapCardSkeleton key={skeletonKey} />
          ))}
        </Box>
      );
    }
    if (filteredMaps.length === 0) {
      return (
        <Card sx={{ p: 5, textAlign: "center" }}>
          <MapIcon sx={{ fontSize: 40, color: tokens.text3, mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.text2 }}>
            {venueFilterId ? "No maps for this venue" : "No maps yet"}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.text3, mt: 0.5 }}>
            {venueFilterId
              ? "This venue has no maps assigned to you."
              : "Maps for your venues will appear here."}
          </Typography>
        </Card>
      );
    }
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 1.5,
        }}
      >
        {filteredMaps.map((m) => (
          <MapCard key={m._id} map={m} onClick={() => navigate(`/maps/${m._id}`)} />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={pageContainerSx()}>
      <PageHead
        title="Maps"
        sub={
          venueFilterId
            ? `Maps for ${venueFilterName || "selected venue"} · view only`
            : "Floor maps for your venues · view only"
        }
        icon="map"
        actions={
          <>
            {/* Venue filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={venueOptions.some((v) => v.id === venueFilterId) ? venueFilterId : ""}
                onChange={(e) => setVenueFilter(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: 13,
                  height: 36,
                  borderRadius: "9px",
                  background: tokens.surface2,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: tokens.border,
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>
                  All venues
                </MenuItem>
                {venueOptions.map((v) => (
                  <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13 }}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Chip
              label="View only"
              size="small"
              sx={{ background: tokens.surface3, color: tokens.text2 }}
            />
          </>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 1.5,
          mb: 1.75,
        }}
      >
        {[
          { icon: MapIcon, label: "Maps", value: filteredMaps.length },
          { icon: PlaceIcon, label: "Marked points", value: totalMarkers },
          { icon: SmartToyIcon, label: "Robots deployed", value: totalRobots },
        ].map(({ icon: Icon, label, value }) => (
          <Card key={label} sx={{ p: "13px 14px" }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  background: tokens.accentSoft,
                  color: tokens.accent,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Icon sx={{ fontSize: 15 }} />
              </Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: tokens.text2 }}>
                {label}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-.03em" }}>
              {loading ? "—" : value}
            </Typography>
          </Card>
        ))}
      </Box>

      {renderMapGrid()}
    </Box>
  );
};

const mapShape = PropTypes.shape({
  _id: PropTypes.string,
  name: PropTypes.string,
  thumb: PropTypes.string,
  thumbType: PropTypes.string,
  markerCount: PropTypes.number,
  robotCount: PropTypes.number,
  venue: PropTypes.string,
  floor: PropTypes.string,
  updatedAt: PropTypes.string,
});

MapThumb.propTypes = {
  map: mapShape.isRequired,
};

MapCard.propTypes = {
  map: mapShape.isRequired,
  onClick: PropTypes.func,
};

Sk.propTypes = {
  sx: PropTypes.object,
};

export default Maps;
